/**
 * Игровой прогресс: четыре независимых показателя и XP.
 *
 * ТЗ §4.2. Покупка подписки не даёт XP и не влияет на рейтинг (FR-GM-06).
 */

import type { LanguageCode } from './language.js';

/** Причины начисления XP. Список закрыт: произвольного начисления не существует. */
export const XP_REASONS = [
  'session-completed',
  'accuracy-improved',
  'new-unit-mastered',
  'persistent-error-fixed',
  'control-test-completed',
  'scheduled-review-done',
  'skill-retained',
] as const;
export type XpReason = (typeof XP_REASONS)[number];

/** Запись журнала XP. Источник истины — сервер (FR-GM-05). */
export interface XpTransaction {
  readonly reason: XpReason;
  readonly amount: number;
  /** Календарная метка начисления (ISO 8601). */
  readonly awardedAt: string;
  /** Коэффициент diminishing returns, применённый к базовой величине, 0..1. */
  readonly diminishingFactor: number;
}

/** Четыре независимых показателя прогресса (FR-GM-01). */
export interface ProgressIndicators {
  /** Подтверждённый навык печати, 0..1 относительно целевого уровня. */
  readonly skill: number;
  /** Улучшение относительно baseline, 0..1 после сглаживания. */
  readonly growth: number;
  /** Накопленный XP. */
  readonly xp: number;
  /** Регулярность и сохранение навыка, 0..1. */
  readonly consistency: number;
}

/** Целевые уровни владения навыком (ТЗ §1.3). */
export interface SkillTier {
  readonly name: 'beginner' | 'confident' | 'fast' | 'pro';
  readonly wpm: number;
  readonly accuracy: number;
}

export const SKILL_TIERS: readonly SkillTier[] = Object.freeze([
  { name: 'beginner', wpm: 20, accuracy: 0.94 },
  { name: 'confident', wpm: 35, accuracy: 0.95 },
  { name: 'fast', wpm: 50, accuracy: 0.96 },
  { name: 'pro', wpm: 70, accuracy: 0.97 },
]);

/** Базовый критерий освоения навыка (ТЗ §1.3). */
export const TARGET_WPM = 35;
export const TARGET_ACCURACY = 0.95;

/** Наглядное сравнение старта и текущего результата (FR-GM-03). */
export interface GrowthReport {
  readonly language: LanguageCode;
  readonly baselineCpm: number;
  readonly currentCpm: number;
  readonly deltaCpm: number;
  /** Во сколько раз выросла скорость. */
  readonly speedMultiplier: number;
  readonly baselineAccuracy: number;
  readonly currentAccuracy: number;
  /** Подтверждён ли текущий результат на незнакомом тексте. */
  readonly verifiedOnUnseenText: boolean;
}
